import { Analyst } from '../../../../types/Analyst'
import { AnalystMutation, IntercomAdmin } from '../types/AnalystMutation'

export type AnalystFormValues = {
  name: string
  title: string
  // '' when there is no picture.
  picture: string
  intercomId: string | null
  inactive: boolean
}

export type IntercomAdminOption = { id: string | null; label: string }

const compareCodeUnits = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// Active analysts first, then by name, compared by character code like lodash `sortBy`.
export const sortAnalysts = (analysts: Analyst[]) =>
  [...analysts].sort((a, b) => Number(!!a.inactive) - Number(!!b.inactive) || compareCodeUnits(a.name ?? '', b.name ?? ''))

export const toAnalystFormValues = (analyst?: Analyst): AnalystFormValues =>
  analyst
    ? {
        name: analyst.name ?? '',
        title: analyst.title ?? '',
        picture: analyst.picture ?? '',
        intercomId: analyst.intercomId ?? null,
        inactive: !!analyst.inactive,
      }
    : { name: '', title: '', picture: '', intercomId: null, inactive: true }

export const toAnalystMutation = (original: Analyst | undefined, values: AnalystFormValues): AnalystMutation => {
  const edited = { name: values.name, title: values.title, intercomId: values.intercomId, inactive: values.inactive }
  if (original) return { id: original.id, ...edited, picture: values.picture || (original.picture === '' ? '' : null) }
  return { id: null, ...edited, ...(values.picture ? { picture: values.picture } : {}) }
}

// Only admins with an Intercom inbox seat can answer Ask Analyst. A stored admin that is no longer listed is kept as an option, so opening the dialog doesn't clear it.
export const getIntercomAdminOptions = (admins: IntercomAdmin[], currentId: string | null, disabledLabel: string): IntercomAdminOption[] => {
  const options: IntercomAdminOption[] = [
    { id: null, label: disabledLabel },
    ...admins.filter((admin) => admin.has_inbox_seat).map((admin) => ({ id: String(admin.id), label: `${admin.name} (${admin.email ?? ''})` })),
  ]
  return currentId && !options.some((option) => option.id === currentId) ? [...options, { id: currentId, label: currentId }] : options
}

export type AnalystField = 'name' | 'title' | 'picture' | 'intercomId' | 'inactive'

export const analystFieldLabelKeys: Record<AnalystField, string> = {
  name: 'internal-analysts:analysts_name',
  title: 'internal-analysts:analysts_title',
  picture: 'internal-analysts:analysts_picture',
  intercomId: 'internal-analysts:analysts_intercom_id',
  inactive: 'internal-analysts:analysts_inactive',
}

export const getUnstoredAnalystFields = (sent: AnalystMutation, stored: Analyst): AnalystField[] => {
  const unstored: AnalystField[] = []
  if ((stored.name ?? '') !== sent.name) unstored.push('name')
  if ((stored.title ?? '') !== sent.title) unstored.push('title')
  if ((stored.picture || null) !== (sent.picture || null)) unstored.push('picture')
  if ((stored.intercomId ?? null) !== sent.intercomId) unstored.push('intercomId')
  if (!!stored.inactive !== sent.inactive) unstored.push('inactive')
  return unstored
}
